import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, useFieldArray } from "react-hook-form";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { uploadFile } from "../../../api/upload-file";
import { addEvent, updateEvent } from "../../../api/event";
import { addEventSuccess, updateEventSuccess } from "../../../slice/events";
import { toast } from "react-toastify";
import moment from "moment";

// Event.name / desc / image are LocalizedText { en, ar? } subdocs. Legacy
// data may still be plain strings until the BE migration runs.
const readEn = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v.en ?? "";
};
const readAr = (v) => {
  if (!v || typeof v === "string") return "";
  return v.ar ?? "";
};

const buildLocalized = (en, ar, { required } = { required: false }) => {
  const enTrim = (en ?? "").trim();
  const arTrim = (ar ?? "").trim();
  if (!enTrim && !required) return undefined;
  return arTrim ? { en: enTrim, ar: arTrim } : { en: enTrim };
};

const toTimeString = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return moment(value).format("HH:mm");
};

const parseEventToForm = (event) => {
  const base = {
    nameEn: readEn(event?.name),
    nameAr: readAr(event?.name),
    descEn: readEn(event?.desc),
    descAr: readAr(event?.desc),
    imageEn: readEn(event?.image),
    imageAr: readAr(event?.image),
    location: event?.location ?? "",
    link: event?.link ?? "",
  };
  if (!event?.dates) return { ...base, dates: [] };

  const dates = event.dates.map((item) => {
    if (!item || typeof item !== "object") return item;

    const startDateValue = item?.start?.date
      ? moment(item.start.date)
      : item?.date
      ? moment(item.date)
      : item?.from
      ? moment(item.from)
      : item?.to
      ? moment(item.to)
      : null;
    const endDateValue = item?.end?.date
      ? moment(item.end.date)
      : item?.to
      ? moment(item.to)
      : item?.date
      ? moment(item.date)
      : item?.from
      ? moment(item.from)
      : startDateValue;

    const startTime = item?.start?.time
      ? toTimeString(item.start.time)
      : item?.startTime
      ? toTimeString(item.startTime)
      : item?.from
      ? toTimeString(item.from)
      : null;
    const endTime = item?.end?.time
      ? toTimeString(item.end.time)
      : item?.endTime
      ? toTimeString(item.endTime)
      : item?.to
      ? toTimeString(item.to)
      : null;

    return {
      start: {
        date: startDateValue ? startDateValue.toDate() : null,
        time: startTime,
      },
      end: {
        date: endDateValue ? endDateValue.toDate() : null,
        time: endTime,
      },
    };
  });

  return { ...base, dates };
};

const useEventForm = (event, { closeModal }) => {
  const timeSchema = yup
    .string()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === null ||
        originalValue === "null" ||
        originalValue === ""
      ) {
        return null;
      }
      return value;
    })
    .matches(/^([01]\d|2[0-3]):[0-5]\d$/, {
      message: "Invalid time",
      excludeEmptyString: true,
    });

  const dateTimeSchema = yup.object({
    date: yup.date().required("Date is required").typeError("Invalid date"),
    time: timeSchema,
  });

  const schema = yup
    .object({
      nameEn: yup.string().trim().required("Name (English) is required"),
      nameAr: yup.string().trim().nullable(),
      descEn: yup.string().trim().nullable(),
      descAr: yup.string().trim().nullable(),
      imageEn: yup.string().required("Image (English) is required"),
      imageAr: yup.string().nullable(),
      location: yup.string().required("Location is required"),
      link: yup.string(),
      dates: yup
        .array()
        .required("At least one date is required")
        .min(1, "At least one date is required")
        .of(
          yup.object({
            start: dateTimeSchema,
            end: dateTimeSchema.test(
              "end-date-after-start",
              "End date cannot be before the start date",
              function (value) {
                const start = this.parent?.start;
                if (!start?.date || !value?.date) return true;
                return moment(value.date).isSameOrAfter(start.date, "day");
              }
            ),
          })
        ),
    })
    .required();

  const defaultValues = parseEventToForm(event);

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    shouldFocusError: false,
  });

  const { control, watch, setValue, reset } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "dates",
  });

  const onFormSubmit = async (data) => {
    const dates = data.dates.map((item) => {
      return {
        start: {
          date: item.start?.date ? moment(item.start.date).toDate() : null,
          time: item.start?.time ? item.start.time : undefined,
        },
        end: {
          date: item.end?.date ? moment(item.end.date).toDate() : null,
          time: item.end?.time ? item.end.time : undefined,
        },
      };
    });
    const payload = {
      name: buildLocalized(data.nameEn, data.nameAr, { required: true }),
      desc: buildLocalized(data.descEn, data.descAr),
      image: buildLocalized(data.imageEn, data.imageAr, { required: true }),
      location: data.location,
      link: data.link,
      dates,
    };
    if (event) {
      await onUpdateEvent(payload);
    } else {
      await onAddEvent(payload);
    }
  };

  const imageEn = watch("imageEn");
  const imageAr = watch("imageAr");
  useEffect(() => {
    reset(parseEventToForm(event));
  }, [reset, event]);

  const dispatch = useDispatch();
  const [loadingImageField, setLoadingImageField] = useState(null);
  const uploadImage = async (payload, field) => {
    setLoadingImageField(field);
    try {
      const res = await uploadFile(payload);
      setValue(field, res.fileUrl);
    } catch (error) {
      toast.error(error);
    }
    setLoadingImageField(null);
  };
  const onAddEvent = async (data) => {
    try {
      const res = await addEvent(data);
      dispatch(addEventSuccess(res));
      toast.success("Add event successfully");
      closeModal();
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const onUpdateEvent = async (data) => {
    try {
      const res = await updateEvent(event._id, data);
      const updated = res?.event ?? res?.data ?? res;
      dispatch(updateEventSuccess(updated));
      toast.success("Update event successfully");
      closeModal();
    } catch (error) {
      toast.error(error?.message);
    }
  };
  return {
    form,
    onFormSubmit,
    fields,
    append,
    remove,
    control,
    loadingImageField,
    imageEn,
    imageAr,
    uploadImage,
  };
};

export default useEventForm;
